import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";

// Alerts are computed live server-side but backed by fleet_alert_events'
// persisted lifecycle -- nothing pushes a notification when one opens, so
// polling is the only way any page reflects a fresh HOS violation, off-route
// event, etc. without a manual click.
//
// SHARED SINGLETON POLLER (2026-10-13): every component that calls useAlerts
// now subscribes to ONE /scripted/fleet/alerts fetch loop instead of each
// starting its own. Before this, ~6 components (DispatcherLayout's shell plus
// whichever dashboard page was open: ControlTower, DispatcherHome,
// FleetDashboard, DispatcherBoard, Alerts) each ran an independent 60s poll
// with no sharing -- so a single browser made 2-3 concurrent alert calls per
// minute, and across a room of dispatchers those uncoordinated polls stacked
// into a burst every few seconds that helped exhaust the backend DB pool
// (504s on load upload). One timer + one in-flight request + one cache ->
// exactly one call per minute per browser, regardless of how many components
// read alerts. The public return shape is unchanged, so no consumer changes.
//
// Takes apiBaseUrl/authHeaders as params rather than importing them, since
// DispatcherBoard.jsx deliberately keeps its own local copies instead of
// importing pages/dispatcher/shared.js.

const shared = {
  data: null,
  loading: true,
  error: null,
  inFlight: null,          // in-flight Promise (dedupes concurrent fetches)
  timer: null,             // the single shared interval
  intervalMs: 60000,
  apiBaseUrl: null,
  authHeaders: null,
};
const subscribers = new Set();

const snapshot = () => ({ data: shared.data, loading: shared.loading, error: shared.error });
const notify = () => {
  const s = snapshot();
  subscribers.forEach((cb) => cb(s));
};

function fetchAlerts(silent) {
  if (shared.inFlight) return shared.inFlight;            // dedupe concurrent callers
  if (!shared.apiBaseUrl || !shared.authHeaders) return Promise.resolve();
  if (!silent) { shared.loading = true; notify(); }
  shared.inFlight = axios
    .get(`${shared.apiBaseUrl}/scripted/fleet/alerts`, shared.authHeaders())
    .then((res) => { shared.data = res.data; shared.error = null; })
    .catch((err) => { shared.error = err?.response?.data?.detail || err?.message || "error"; })
    .finally(() => {
      shared.inFlight = null;
      shared.loading = false;
      notify();
    });
  return shared.inFlight;
}

function startTimer() {
  if (shared.timer) return;
  shared.timer = setInterval(() => fetchAlerts(true), shared.intervalMs);
}
function stopTimer() {
  if (shared.timer) { clearInterval(shared.timer); shared.timer = null; }
}

export function useAlerts(apiBaseUrl, authHeaders, { intervalMs = 60000 } = {}) {
  const [, forceRender] = useState(0);
  const localRef = useRef(snapshot());

  // Keep the shared fetcher's config fresh on every render (cheap; no deps) so
  // a token refresh / base-url change is picked up by the single poller even
  // though we don't re-subscribe for it.
  useEffect(() => {
    shared.apiBaseUrl = apiBaseUrl;
    shared.authHeaders = authHeaders;
    shared.intervalMs = intervalMs;
  });

  // Subscribe to the shared poller; the first subscriber starts it, the last
  // one to leave stops it.
  useEffect(() => {
    shared.apiBaseUrl = apiBaseUrl;
    shared.authHeaders = authHeaders;
    shared.intervalMs = intervalMs;

    const cb = (s) => { localRef.current = s; forceRender((n) => n + 1); };
    subscribers.add(cb);

    if (subscribers.size === 1) {
      startTimer();
      fetchAlerts(false);          // immediate first load when the loop starts
    } else {
      cb(snapshot());              // hand a newcomer the current cache at once
    }

    return () => {
      subscribers.delete(cb);
      if (subscribers.size === 0) stopTimer();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBaseUrl, intervalMs]);

  const reload = useCallback(() => fetchAlerts(false), []);

  const { data, loading, error } = localRef.current;
  const alerts = data?.alerts || [];
  const counts = data?.counts || { critical: 0, warning: 0, info: 0 };
  const total = counts.critical + counts.warning + counts.info;

  return { data, alerts, counts, total, loading, error, reload };
}

// Test-only: reset the module singleton between test cases.
export function __resetAlertsPollerForTests() {
  stopTimer();
  subscribers.clear();
  shared.data = null;
  shared.loading = true;
  shared.error = null;
  shared.inFlight = null;
  shared.apiBaseUrl = null;
  shared.authHeaders = null;
  shared.intervalMs = 60000;
}
